import { useState, useEffect, useMemo } from 'react';
import {
  Card, Row, Col, Button, Table, Tag, Modal, Form, Input, Select, Progress,
  Descriptions, Space, Statistic, message, Tooltip, Badge, Typography, Divider, InputNumber,
} from 'antd';
import {
  ExperimentOutlined, PlusOutlined, TrophyOutlined, PlayCircleOutlined,
  StopOutlined, BarChartOutlined, EyeOutlined, ThunderboltOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { getTests, createTest, declareWinner, endTest, startTest } from '../../api/abtest';

const { TextArea } = Input;
const { Title, Text } = Typography;

// —— 状态配置 ——
const STATUS_MAP = {
  draft: { label: '草稿', color: 'default' },
  running: { label: '运行中', color: 'processing' },
  ended: { label: '已结束', color: 'error' },
  winner_declared: { label: '已获胜', color: 'success' },
};

const TYPE_MAP = {
  creative: { label: '素材测试', color: 'blue' },
  title: { label: '标题测试', color: 'purple' },
  landing: { label: '落地页测试', color: 'green' },
};

// —— 新建实验 Modal ——
const CreateTestModal = ({ open, onCancel, onOk }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (startImmediately = false) => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onOk({ ...values, startImmediately });
      form.resetFields();
    } catch (e) {
      // 表单校验失败不处理
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<Space><ExperimentOutlined style={{ color: '#6366f1' }} />新建A/B测试实验</Space>}
      open={open}
      onCancel={() => { onCancel(); form.resetFields(); }}
      footer={[
        <Button key="cancel" onClick={() => { onCancel(); form.resetFields(); }}>取消</Button>,
        <Button key="draft" onClick={() => handleSubmit(false)} loading={loading}>保存草稿</Button>,
        <Button key="start" type="primary" loading={loading} onClick={() => handleSubmit(true)}>
          <PlayCircleOutlined /> 保存并启动
        </Button>,
      ]}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ testType: 'creative', trafficSplitMode: 'equal', variantCount: 3 }}>
        <Form.Item name="name" label="实验名称" rules={[{ required: true, message: '请输入实验名称' }]}>
          <Input placeholder="例如：Q4黑五促销素材测试" maxLength={50} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="testType" label="测试类型" rules={[{ required: true }]}>
              <Select options={[
                { value: 'creative', label: '素材测试' },
                { value: 'title', label: '标题测试' },
                { value: 'landing', label: '落地页测试' },
              ]} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="trafficSplitMode" label="流量分配">
              <Select options={[
                { value: 'equal', label: '均等分配' },
                { value: 'manual', label: '手动分配' },
              ]} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="variantCount" label="变体数量" rules={[{ required: true, message: '请设置变体数量' }]}>
          <InputNumber min={2} max={6} style={{ width: '100%' }} addonAfter="个变体" />
        </Form.Item>
        <Form.Item name="description" label="实验描述">
          <TextArea rows={3} placeholder="请描述本次实验的测试目标和假设..." maxLength={200} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// —— 测试报告 Modal（Bar图对比转化率）——
const ReportModal = ({ open, onCancel, test }) => {
  if (!test) return null;

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const p = params[0];
        return `${p.name}<br/>转化率: <strong>${p.value}%</strong>`;
      },
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: test.variants.map((v) => v.name),
      axisLabel: { color: 'rgba(240,245,250,0.7)', fontSize: 12 },
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
    },
    yAxis: {
      type: 'value',
      name: '转化率(%)',
      axisLabel: { color: 'rgba(240,245,250,0.6)', formatter: '{value}%' },
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
    },
    series: [{
      type: 'bar',
      data: test.variants.map((v) => ({
        value: v.conversionRate,
        itemStyle: {
          color: v.name === test.winnerVariant
            ? { type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [{ offset: 0, color: '#52c41a' }, { offset: 1, color: '#389e0d' }] }
            : { type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [{ offset: 0, color: '#6366f1' }, { offset: 1, color: '#4f46e5' }] },
          borderRadius: [6, 6, 0, 0],
        },
      })),
      barWidth: '45%',
      label: {
        show: true,
        position: 'top',
        color: 'rgba(240,245,250,0.85)',
        formatter: '{c}%',
      },
      emphasis: {
        itemStyle: { shadowBlur: 10, shadowColor: 'rgba(99,102,241,0.5)' },
      },
    }],
  };

  return (
    <Modal
      title={<Space><BarChartOutlined style={{ color: '#6366f1' }} />测试报告 — {test.name}</Space>}
      open={open}
      onCancel={onCancel}
      footer={<Button onClick={onCancel}>关闭</Button>}
      width={720}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 实验概览 */}
        <Descriptions column={4} size="small" bordered>
          <Descriptions.Item label="测试类型">
            <Tag color={TYPE_MAP[test.type]?.color}>{TYPE_MAP[test.type]?.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={STATUS_MAP[test.status]?.color}>{STATUS_MAP[test.status]?.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="开始时间">{test.startTime || '—'}</Descriptions.Item>
          <Descriptions.Item label="结束时间">{test.endTime || '—'}</Descriptions.Item>
          <Descriptions.Item label="变体数">{test.variants.length}</Descriptions.Item>
          <Descriptions.Item label="胜出变体">
            {test.winnerVariant ? <Tag color="success">{test.winnerVariant}</Tag> : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="置信度">
            <Text strong style={{ color: test.confidence >= 95 ? '#52c41a' : test.confidence >= 80 ? '#faad14' : '#f5222d' }}>
              {test.confidence}%
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="流量分配">
            {test.trafficSplitMode === 'equal' ? '均等' : '手动'}
          </Descriptions.Item>
        </Descriptions>

        {/* Bar 图 */}
        <div>
          <Title level={5} style={{ color: 'var(--text-primary)', marginBottom: 12 }}>各变体转化率对比</Title>
          <ReactECharts option={option} style={{ height: 280 }} />
        </div>

        {/* 变体数据明细 */}
        <div>
          <Title level={5} style={{ color: 'var(--text-primary)', marginBottom: 12 }}>变体核心指标</Title>
          <Table
            size="small"
            pagination={false}
            dataSource={test.variants}
            rowKey="id"
            columns={[
              { title: '变体名称', dataIndex: 'name', key: 'name' },
              { title: '流量占比', dataIndex: 'trafficRatio', key: 'trafficRatio', render: (v) => `${v}%` },
              { title: '曝光量', dataIndex: 'impressions', key: 'impressions', render: (v) => v.toLocaleString() },
              { title: '点击数', dataIndex: 'clicks', key: 'clicks', render: (v) => v.toLocaleString() },
              { title: '转化数', dataIndex: 'conversions', key: 'conversions', render: (v) => v.toLocaleString() },
              {
                title: '转化率', dataIndex: 'conversionRate', key: 'conversionRate',
                render: (v, record) => (
                  <Text style={{
                    color: record.name === test.winnerVariant ? '#52c41a' : 'inherit',
                    fontWeight: record.name === test.winnerVariant ? 600 : 400,
                  }}>
                    {v}%
                  </Text>
                ),
              },
            ]}
          />
        </div>
      </Space>
    </Modal>
  );
};

// —— 主页面 ——
const ABTestPage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [reportTest, setReportTest] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [winnerModal, setWinnerModal] = useState({ open: false, test: null, variant: null });

  // 加载数据
  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getTests();
      setTests(data);
    } catch (e) {
      message.error('加载测试数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // 统计数据
  const stats = useMemo(() => {
    const total = tests.length;
    const running = tests.filter((t) => t.status === 'running').length;
    const endedWithWinner = tests.filter((t) => t.status === 'winner_declared');
    const avgConfidence = total > 0
      ? (tests.reduce((sum, t) => sum + t.confidence, 0) / total).toFixed(1)
      : '0.0';
    const winnerRate = total > 0
      ? ((endedWithWinner.length / total) * 100).toFixed(1)
      : '0.0';
    return { total, running, avgConfidence, winnerRate };
  }, [tests]);

  // 创建实验
  const handleCreate = async (values) => {
    const isStart = values.startImmediately;
    try {
      await createTest(values);
      message.success(isStart ? '实验已启动' : '草稿已保存');
      setCreateOpen(false);
      loadData();
    } catch (e) {
      message.error('创建失败');
    }
  };

  // 启动草稿实验
  const handleStart = async (id) => {
    try {
      await startTest(id);
      message.success('实验已启动');
      loadData();
    } catch (e) {
      message.error('启动失败');
    }
  };

  // 结束实验
  const handleEnd = (record) => {
    Modal.confirm({
      title: '确认结束实验',
      content: `确定要结束「${record.name}」吗？结束后将不再收集数据。`,
      okText: '确认结束',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await endTest(record.id);
          message.success('实验已结束');
          loadData();
        } catch (e) {
          message.error('操作失败');
        }
      },
    });
  };

  // 宣布胜出
  const handleDeclareWinner = async () => {
    const { test, variant } = winnerModal;
    try {
      await declareWinner(test.id, variant);
      message.success(`已宣布「${variant}」为胜出变体`);
      setWinnerModal({ open: false, test: null, variant: null });
      loadData();
    } catch (e) {
      message.error('操作失败');
    }
  };

  // 查看测试报告
  const handleViewReport = (record) => {
    setReportTest(record);
    setReportOpen(true);
  };

  // 展开行：变体详情
  const expandedRowRender = (record) => {
    if (record.variants.length === 0) {
      return <Text type="secondary" style={{ padding: 16, display: 'block' }}>暂无变体数据</Text>;
    }
    return (
      <Descriptions column={7} size="small" style={{ padding: '8px 0' }}>
        {record.variants.map((v, i) => (
          <Descriptions.Item key={v.id} label={
            <Space>
              <Badge color={['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'][i % 6]} />
              {v.name}
              {v.name === record.winnerVariant && <Tag color="success" style={{ marginLeft: 4 }}>胜出</Tag>}
            </Space>
          }>
            <Space split={<Divider type="vertical" />} size={4} style={{ fontSize: 12 }}>
              <span>流量 <strong>{v.trafficRatio}%</strong></span>
              <span>曝光 <strong>{v.impressions.toLocaleString()}</strong></span>
              <span>点击 <strong>{v.clicks.toLocaleString()}</strong></span>
              <span>转化 <strong>{v.conversions.toLocaleString()}</strong></span>
              <span style={{ color: '#6366f1' }}>转化率 <strong>{v.conversionRate}%</strong></span>
            </Space>
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: '实验名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: 'var(--text-primary)' }}>{text}</Text>
          <Space size={4}>
            <Tag color={TYPE_MAP[record.type]?.color} style={{ fontSize: 11 }}>
              {TYPE_MAP[record.type]?.label}
            </Tag>
            {record.confidence >= 95 && record.status === 'running' && (
              <Tag color="success" style={{ fontSize: 11 }}>可宣布胜出</Tag>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={STATUS_MAP[status]?.color}>{STATUS_MAP[status]?.label}</Tag>
      ),
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 160,
      render: (v) => v || '—',
    },
    {
      title: '变体数',
      dataIndex: 'variants',
      key: 'variantCount',
      width: 70,
      render: (v) => <Text strong>{v.length}</Text>,
    },
    {
      title: '胜出变体',
      dataIndex: 'winnerVariant',
      key: 'winnerVariant',
      width: 130,
      render: (v) => v ? <Tag color="success">{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: '置信度',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 160,
      render: (v, record) => (
        <Tooltip title={`置信度 ${v}%`}>
          <Progress
            percent={v}
            size="small"
            showInfo={false}
            strokeColor={v >= 95 ? '#52c41a' : v >= 80 ? '#faad14' : '#6366f1'}
            trailColor="rgba(255,255,255,0.08)"
            style={{ minWidth: 100 }}
          />
          <Text style={{ fontSize: 12, color: v >= 95 ? '#52c41a' : v >= 80 ? '#faad14' : 'rgba(240,245,250,0.6)' }}>
            {v}%
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space size={4}>
          {/* 查看报告 */}
          <Button size="small" icon={<BarChartOutlined />} onClick={() => handleViewReport(record)}>
            报告
          </Button>

          {/* 草稿：启动 */}
          {record.status === 'draft' && (
            <Button size="small" type="primary" ghost icon={<PlayCircleOutlined />} onClick={() => handleStart(record.id)}>
              启动
            </Button>
          )}

          {/* 运行中：宣布胜出 */}
          {record.status === 'running' && (
            <Button
              size="small"
              type={record.confidence >= 95 ? 'primary' : 'default'}
              icon={<TrophyOutlined />}
              style={record.confidence >= 95 ? {
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                borderColor: '#52c41a',
                boxShadow: '0 2px 8px rgba(82,196,26,0.3)',
              } : {}}
              onClick={() => {
                // 找转化率最高的变体作为默认推荐
                const best = [...record.variants].sort((a, b) => b.conversionRate - a.conversionRate)[0];
                setWinnerModal({ open: true, test: record, variant: best?.name || '' });
              }}
            >
              宣布胜出
            </Button>
          )}

          {/* 运行中：结束 */}
          {record.status === 'running' && (
            <Button size="small" danger ghost icon={<StopOutlined />} onClick={() => handleEnd(record)}>
              结束
            </Button>
          )}

          {/* 已结束：查看胜出（如果有） */}
          {(record.status === 'ended' || record.status === 'winner_declared') && record.winnerVariant && (
            <Tag color="success" icon={<TrophyOutlined />}>{record.winnerVariant}</Tag>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ color: 'var(--text-primary)', margin: 0 }}>
          <ExperimentOutlined style={{ marginRight: 8, color: '#6366f1' }} />
          A/B 测试中心
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          科学对比实验，数据驱动决策 — 对标巨量引擎A/B测试
        </Text>
      </div>

      {/* 顶部统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)' }}>
            <Statistic
              title={<span style={{ color: 'rgba(240,245,250,0.6)' }}>总测试数</span>}
              value={stats.total}
              prefix={<ExperimentOutlined style={{ color: '#6366f1' }} />}
              valueStyle={{ color: 'var(--text-primary)' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)' }}>
            <Statistic
              title={<span style={{ color: 'rgba(240,245,250,0.6)' }}>运行中</span>}
              value={stats.running}
              prefix={<PlayCircleOutlined style={{ color: '#10b981' }} />}
              valueStyle={{ color: 'var(--text-primary)' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)' }}>
            <Statistic
              title={<span style={{ color: 'rgba(240,245,250,0.6)' }}>平均置信度</span>}
              value={stats.avgConfidence}
              suffix="%"
              prefix={<PieChartOutlined style={{ color: '#f59e0b' }} />}
              valueStyle={{ color: 'var(--text-primary)' }}
              precision={1}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}>
            <Statistic
              title={<span style={{ color: 'rgba(240,245,250,0.6)' }}>获胜提效率</span>}
              value={stats.winnerRate}
              suffix="%"
              prefix={<ThunderboltOutlined style={{ color: '#ef4444' }} />}
              valueStyle={{ color: 'var(--text-primary)' }}
              precision={1}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作栏 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Text strong style={{ color: 'var(--text-primary)', fontSize: 15 }}>
            <EyeOutlined style={{ marginRight: 6 }} />
            实验列表
          </Text>
          <Badge count={tests.length} overflowCount={99} showZero style={{ background: '#6366f1' }} />
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)} style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          border: '1px solid rgba(139,92,246,0.3)',
          boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
        }}>
          新建实验
        </Button>
      </div>

      {/* 实验列表 */}
      <Card style={{ background: 'rgba(5,15,30,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Table
          columns={columns}
          dataSource={tests}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => record.variants.length > 0,
            expandIcon: ({ expanded, onExpand, record }) =>
              record.variants.length === 0 ? (
                <span style={{ display: 'inline-block', width: 24 }} />
              ) : (
                <Button
                  type="text"
                  size="small"
                  icon={
                    <span style={{
                      display: 'inline-block', transition: 'transform 200ms',
                      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      color: '#6366f1',
                    }}>▶</span>
                  }
                  onClick={(e) => onExpand(record, e)}
                />
              ),
          }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1100 }}
        />
      </Card>

      {/* 新建实验 Modal */}
      <CreateTestModal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
      />

      {/* 测试报告 Modal */}
      <ReportModal
        open={reportOpen}
        onCancel={() => setReportOpen(false)}
        test={reportTest}
      />

      {/* 宣布胜出确认 Modal */}
      <Modal
        title={<Space><TrophyOutlined style={{ color: '#52c41a' }} />宣布胜出变体</Space>}
        open={winnerModal.open}
        onCancel={() => setWinnerModal({ open: false, test: null, variant: null })}
        onOk={handleDeclareWinner}
        okText="确认宣布"
        cancelText="取消"
        okButtonProps={{
          style: { background: '#52c41a', borderColor: '#52c41a' },
        }}
      >
        {winnerModal.test && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>实验：<strong>{winnerModal.test.name}</strong></Text>
            <Text>
              当前置信度：
              <Text strong style={{ color: winnerModal.test.confidence >= 95 ? '#52c41a' : '#faad14' }}>
                {winnerModal.test.confidence}%
              </Text>
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              宣布胜出后，实验将自动结束并标记胜出变体，流量将切换至胜出方案。
            </Text>
            <div>
              <Text style={{ display: 'block', marginBottom: 8 }}>选择胜出变体：</Text>
              <Select
                value={winnerModal.variant}
                onChange={(v) => setWinnerModal((prev) => ({ ...prev, variant: v }))}
                style={{ width: '100%' }}
                options={winnerModal.test.variants.map((v) => ({
                  value: v.name,
                  label: `${v.name}（转化率 ${v.conversionRate}%）`,
                }))}
                placeholder="请选择胜出变体"
              />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default ABTestPage;
